import type { AbilityName } from '@character-creator/types';

export interface Weapon {
  id: string;
  name: string;
  damage: string; // e.g., "1d8"
  damageType: string; // e.g., "slashing", "piercing", "bludgeoning"
  properties: WeaponProperty[];
  category: 'simple' | 'martial';
  type: 'melee' | 'ranged';
}

export type WeaponProperty =
  | 'finesse'
  | 'light'
  | 'heavy'
  | 'two-handed'
  | 'versatile'
  | 'thrown'
  | 'ammunition'
  | 'loading'
  | 'reach'
  | 'special';

/**
 * SRD 5.1 Weapons
 */
export const weapons: Weapon[] = [
  // Simple Melee Weapons
  { id: 'club', name: 'Club', damage: '1d4', damageType: 'bludgeoning', properties: ['light'], category: 'simple', type: 'melee' },
  { id: 'dagger', name: 'Dagger', damage: '1d4', damageType: 'piercing', properties: ['finesse', 'light', 'thrown'], category: 'simple', type: 'melee' },
  { id: 'greatclub', name: 'Greatclub', damage: '1d8', damageType: 'bludgeoning', properties: ['two-handed'], category: 'simple', type: 'melee' },
  { id: 'handaxe', name: 'Handaxe', damage: '1d6', damageType: 'slashing', properties: ['light', 'thrown'], category: 'simple', type: 'melee' },
  { id: 'javelin', name: 'Javelin', damage: '1d6', damageType: 'piercing', properties: ['thrown'], category: 'simple', type: 'melee' },
  { id: 'light-hammer', name: 'Light Hammer', damage: '1d4', damageType: 'bludgeoning', properties: ['light', 'thrown'], category: 'simple', type: 'melee' },
  { id: 'mace', name: 'Mace', damage: '1d6', damageType: 'bludgeoning', properties: [], category: 'simple', type: 'melee' },
  { id: 'quarterstaff', name: 'Quarterstaff', damage: '1d6', damageType: 'bludgeoning', properties: ['versatile'], category: 'simple', type: 'melee' },
  { id: 'sickle', name: 'Sickle', damage: '1d4', damageType: 'slashing', properties: ['light'], category: 'simple', type: 'melee' },
  { id: 'spear', name: 'Spear', damage: '1d6', damageType: 'piercing', properties: ['thrown', 'versatile'], category: 'simple', type: 'melee' },

  // Simple Ranged Weapons
  { id: 'light-crossbow', name: 'Light Crossbow', damage: '1d8', damageType: 'piercing', properties: ['ammunition', 'loading', 'two-handed'], category: 'simple', type: 'ranged' },
  { id: 'dart', name: 'Dart', damage: '1d4', damageType: 'piercing', properties: ['finesse', 'thrown'], category: 'simple', type: 'ranged' },
  { id: 'shortbow', name: 'Shortbow', damage: '1d6', damageType: 'piercing', properties: ['ammunition', 'two-handed'], category: 'simple', type: 'ranged' },
  { id: 'sling', name: 'Sling', damage: '1d4', damageType: 'bludgeoning', properties: ['ammunition'], category: 'simple', type: 'ranged' },

  // Martial Melee Weapons
  { id: 'battleaxe', name: 'Battleaxe', damage: '1d8', damageType: 'slashing', properties: ['versatile'], category: 'martial', type: 'melee' },
  { id: 'flail', name: 'Flail', damage: '1d8', damageType: 'bludgeoning', properties: [], category: 'martial', type: 'melee' },
  { id: 'glaive', name: 'Glaive', damage: '1d10', damageType: 'slashing', properties: ['heavy', 'reach', 'two-handed'], category: 'martial', type: 'melee' },
  { id: 'greataxe', name: 'Greataxe', damage: '1d12', damageType: 'slashing', properties: ['heavy', 'two-handed'], category: 'martial', type: 'melee' },
  { id: 'greatsword', name: 'Greatsword', damage: '2d6', damageType: 'slashing', properties: ['heavy', 'two-handed'], category: 'martial', type: 'melee' },
  { id: 'halberd', name: 'Halberd', damage: '1d10', damageType: 'slashing', properties: ['heavy', 'reach', 'two-handed'], category: 'martial', type: 'melee' },
  { id: 'lance', name: 'Lance', damage: '1d12', damageType: 'piercing', properties: ['reach', 'special'], category: 'martial', type: 'melee' },
  { id: 'longsword', name: 'Longsword', damage: '1d8', damageType: 'slashing', properties: ['versatile'], category: 'martial', type: 'melee' },
  { id: 'maul', name: 'Maul', damage: '2d6', damageType: 'bludgeoning', properties: ['heavy', 'two-handed'], category: 'martial', type: 'melee' },
  { id: 'morningstar', name: 'Morningstar', damage: '1d8', damageType: 'piercing', properties: [], category: 'martial', type: 'melee' },
  { id: 'pike', name: 'Pike', damage: '1d10', damageType: 'piercing', properties: ['heavy', 'reach', 'two-handed'], category: 'martial', type: 'melee' },
  { id: 'rapier', name: 'Rapier', damage: '1d8', damageType: 'piercing', properties: ['finesse'], category: 'martial', type: 'melee' },
  { id: 'scimitar', name: 'Scimitar', damage: '1d6', damageType: 'slashing', properties: ['finesse', 'light'], category: 'martial', type: 'melee' },
  { id: 'shortsword', name: 'Shortsword', damage: '1d6', damageType: 'piercing', properties: ['finesse', 'light'], category: 'martial', type: 'melee' },
  { id: 'trident', name: 'Trident', damage: '1d6', damageType: 'piercing', properties: ['thrown', 'versatile'], category: 'martial', type: 'melee' },
  { id: 'war-pick', name: 'War Pick', damage: '1d8', damageType: 'piercing', properties: [], category: 'martial', type: 'melee' },
  { id: 'warhammer', name: 'Warhammer', damage: '1d8', damageType: 'bludgeoning', properties: ['versatile'], category: 'martial', type: 'melee' },
  { id: 'whip', name: 'Whip', damage: '1d4', damageType: 'slashing', properties: ['finesse', 'reach'], category: 'martial', type: 'melee' },

  // Martial Ranged Weapons
  { id: 'blowgun', name: 'Blowgun', damage: '1', damageType: 'piercing', properties: ['ammunition', 'loading'], category: 'martial', type: 'ranged' },
  { id: 'hand-crossbow', name: 'Hand Crossbow', damage: '1d6', damageType: 'piercing', properties: ['ammunition', 'light', 'loading'], category: 'martial', type: 'ranged' },
  { id: 'heavy-crossbow', name: 'Heavy Crossbow', damage: '1d10', damageType: 'piercing', properties: ['ammunition', 'heavy', 'loading', 'two-handed'], category: 'martial', type: 'ranged' },
  { id: 'longbow', name: 'Longbow', damage: '1d8', damageType: 'piercing', properties: ['ammunition', 'heavy', 'two-handed'], category: 'martial', type: 'ranged' },
  { id: 'net', name: 'Net', damage: '0', damageType: 'none', properties: ['special', 'thrown'], category: 'martial', type: 'ranged' },
];

/**
 * Get weapon by ID
 */
export function getWeapon(id: string): Weapon | undefined {
  return weapons.find(w => w.id === id);
}

/**
 * Get weapon by name (case-insensitive, partial match)
 */
export function findWeaponByName(name: string): Weapon | undefined {
  const normalized = name.toLowerCase().trim();
  // Try exact match first
  const exact = weapons.find(w => w.name.toLowerCase() === normalized || w.id === normalized);
  if (exact) return exact;
  // Try partial match
  return weapons.find(w =>
    w.name.toLowerCase().includes(normalized) ||
    normalized.includes(w.name.toLowerCase())
  );
}

/**
 * Determine which ability modifier to use for a weapon attack
 * - Ranged weapons use DEX
 * - Finesse weapons can use STR or DEX (we'll pick the higher one)
 * - Other melee weapons use STR
 */
export function getWeaponAbility(
  weapon: Weapon,
  strMod: number,
  dexMod: number
): { ability: AbilityName; modifier: number } {
  if (weapon.type === 'ranged') {
    return { ability: 'dexterity', modifier: dexMod };
  }
  if (weapon.properties.includes('finesse')) {
    // Use whichever is higher
    return dexMod >= strMod
      ? { ability: 'dexterity', modifier: dexMod }
      : { ability: 'strength', modifier: strMod };
  }
  return { ability: 'strength', modifier: strMod };
}

/**
 * Format attack bonus as string (e.g., "+5" or "-1")
 */
export function formatAttackBonus(bonus: number): string {
  return bonus >= 0 ? `+${bonus}` : `${bonus}`;
}

/**
 * Format damage string (e.g., "1d8+3 slashing")
 */
export function formatDamage(weapon: Weapon, modifier: number): string {
  if (weapon.damage === '0') return 'Special';
  const modStr = modifier >= 0 ? `+${modifier}` : `${modifier}`;
  return `${weapon.damage}${modStr} ${weapon.damageType}`;
}

/**
 * Valid "any-*" weapon tokens that appear in starting equipment
 */
const ANY_WEAPON_TOKENS: Record<string, { category: 'simple' | 'martial'; type?: 'melee' | 'ranged' }> = {
  'any-simple': { category: 'simple' },
  'any-simple-melee': { category: 'simple', type: 'melee' },
  'any-martial': { category: 'martial' },
  'any-martial-melee': { category: 'martial', type: 'melee' },
};

/**
 * Check if a string is a weapon category token (e.g., "any-martial-melee").
 * Returns false for non-weapon tokens like "any-musical-instrument".
 */
export function isAnyWeaponToken(id: string): boolean {
  return id in ANY_WEAPON_TOKENS;
}

/**
 * Get all weapons matching a category token (e.g., "any-martial-melee" → all martial melee weapons).
 * Returns empty array for unrecognized tokens.
 */
export function getWeaponsForToken(token: string): Weapon[] {
  const filter = ANY_WEAPON_TOKENS[token];
  if (!filter) return [];
  return weapons.filter(
    (w) => w.category === filter.category && (!filter.type || w.type === filter.type)
  );
}

/**
 * Convert a weapon category token to a human-readable label.
 */
export function formatWeaponTokenLabel(token: string): string {
  const labels: Record<string, string> = {
    'any-simple': 'Any Simple Weapon',
    'any-simple-melee': 'Any Simple Melee Weapon',
    'any-martial': 'Any Martial Weapon',
    'any-martial-melee': 'Any Martial Melee Weapon',
  };
  return labels[token] ?? token;
}
